import React from "react";
import { PageHeader } from "@/components/ui-blocks/PageHeader";
import { SEO } from "@/lib/seo";
import { CheckCircle2, ShieldCheck, ScrollText, Landmark, Users, ClipboardList } from "lucide-react";

const BOARD = [
  { name: "Suresh Iyer", role: "Founder & President", bio: "Former civil servant with 22 years in rural administration." },
  { name: "Priya Menon", role: "Executive Director", bio: "Ex-management consultant leading programs and operations." },
  { name: "Anand Kulkarni", role: "Chief Finance Officer", bio: "Chartered Accountant. Governance, audit and compliance." },
  { name: "Dr. Kavita Rao", role: "Medical Director", bio: "Public-health physician. Oversees medical camps." },
  { name: "Ritu Sharma", role: "Head of Partnerships", bio: "CSR, government and multilateral partnerships." },
  { name: "Arjun Deshmukh", role: "Head of Programs", bio: "15 years in disaster response and public health." },
];

const COMMITTEES = [
  { name: "Audit & Finance Committee", desc: "Independent oversight of financial reporting and audit.", meets: "Quarterly" },
  { name: "Programs Committee", desc: "Program design, quality and M&E.", meets: "Monthly" },
  { name: "Grievance Redressal Committee", desc: "Independent review of grievances and complaints.", meets: "On-demand + monthly" },
  { name: "Internal Complaints Committee (POSH)", desc: "Workplace safety and prevention of sexual harassment.", meets: "As required by law" },
  { name: "Child Protection Committee", desc: "POCSO oversight and safeguarding standards.", meets: "Quarterly" },
];

export default function GovernancePage() {
  return (
    <main>
      <SEO title="Governance" description="CAF governance — board, committees, policies and compliance." breadcrumbs={[{name: "Home", href:"/"},{name:"Governance", href:"/governance"}]} />
      <PageHeader eyebrow="Governance" title="Governance built for public trust." lead="Independent board. Structured committees. Full disclosure. Regular audits." />
      <section className="container-wide py-12 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-8">
          <div className="rounded-2xl border border-border bg-background p-6">
            <div className="eyebrow flex items-center gap-2"><Users className="h-4 w-4" /> Board of Trustees</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {BOARD.map((b) => (
                <div key={b.name} className="rounded-lg border border-border p-4 bg-[hsl(var(--surface-tint))]">
                  <div className="font-semibold text-foreground">{b.name}</div>
                  <div className="text-xs text-[hsl(var(--green))] font-semibold">{b.role}</div>
                  <div className="text-xs text-[hsl(var(--muted-ink))] mt-1.5">{b.bio}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <div className="eyebrow flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Committees</div>
            <ul className="mt-4 space-y-3 text-sm">
              {COMMITTEES.map((c) => (
                <li key={c.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-border p-3 bg-background">
                  <div><div className="font-semibold text-foreground">{c.name}</div><div className="text-xs text-[hsl(var(--muted-ink))]">{c.desc}</div></div>
                  <span className="text-xs badge-navy border rounded-full px-2 py-0.5 whitespace-nowrap">Meets: {c.meets}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-2xl border border-border bg-[hsl(var(--surface-tint))] p-6">
            <div className="eyebrow flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Compliance</div>
            <ul className="mt-3 text-sm space-y-2">
              {["Section 8 Company — Companies Act, 2013 (incorporated 2026)","12A — registered","80G — registered","CSR-1 — registered","FCRA — in progress","PAN, TAN — active"].map((c) => (<li key={c} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--green))]" /> {c}</li>))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <div className="eyebrow flex items-center gap-2"><Landmark className="h-4 w-4" /> External audit</div>
            <p className="text-sm text-[hsl(var(--muted-ink))] mt-2">Independent statutory audit as mandated for Section 8 companies. First audited annual report will be published for FY 2026-27 and shared with CSR partners.</p>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <div className="eyebrow flex items-center gap-2"><ScrollText className="h-4 w-4" /> Governance documents</div>
            <ul className="mt-3 text-sm space-y-2">
              {[["/code-of-ethics","Code of Ethics"],["/conflict-of-interest","Conflict of Interest Policy"],["/child-protection","Child Protection Policy"],["/volunteer-policy","Volunteer Policy"],["/accessibility","Accessibility Statement"],["/grievance-redressal","Grievance Redressal"]].map(([to, label]) => (<li key={to}><a href={to} className="text-foreground underline decoration-[hsl(var(--orange))] underline-offset-4">{label}</a></li>))}
            </ul>
          </div>
        </aside>
      </section>
    </main>
  );
}
